/*!
 * bz-game-boot.js — storefront entry point for The Focus Challenge (session 32D)
 *
 * The harness (harness.html / play.html) has its own inline boot for reviewing. THIS is the one
 * that runs on the Shopify page: it is what `bz-game.liquid` calls, and it is deliberately a real
 * asset rather than inline script so it can be cache-busted and syntax-checked like everything else.
 *
 * Phase 1 flow:  play → score → comparative percentile → preference question → product → checkout.
 * NO email, NO login, NO reward — those are Phase 2.
 *
 * DEGRADES BY DESIGN. Everything below the score is local; only the percentile needs the Worker.
 * If /api/game-start or /api/game-run is unreachable, slow, or not yet deployed, the player still
 * gets their score, the preference question and both checkout buttons — they just see the
 * "still building the baseline" line instead of a comparison. That is what lets the storefront ship
 * before the Worker without a second release.
 */
(function (root) {
  'use strict';

  var API_TIMEOUT_MS = 6000;

  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }

  function withTimeout(p, ms) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () { reject(new Error('timeout')); }, ms);
      p.then(function (v) { clearTimeout(t); resolve(v); },
             function (e) { clearTimeout(t); reject(e); });
    });
  }

  function postJSON(url, body) {
    return withTimeout(fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    }), API_TIMEOUT_MS);
  }

  /**
   * Record the finished run and read back the comparison.
   * Resolves to null on ANY failure — a missing percentile is a degraded result, never a broken one.
   */
  function recordRun(endpoint, market, results) {
    if (!endpoint) return Promise.resolve(null);
    return postJSON(endpoint + '/api/game-start', {})
      .then(function (start) {
        if (!start || !start.runToken) throw new Error('no token');
        // Send only what the server needs to validate timing. No per-trial detail, no identity.
        var rounds = results.map(function (r) {
          return { slot: r.slot, roundMs: Math.round(r.roundMs || 0), adaptive: !!r.adaptive };
        });
        return postJSON(endpoint + '/api/game-run', {
          runToken: start.runToken,
          market: market,
          rounds: rounds,
          score: root.BZGame.compositeScore(results)
        });
      })
      .catch(function (e) {
        if (root.console) root.console.warn('[bz-game] scoreboard unavailable:', e.message);
        return null;
      });
  }

  function renderResult(mount, set, market, out, api, restart) {
    mount.innerHTML = '';
    var wrap = el('div', 'bzg-result');

    wrap.appendChild(el('h2', 'bzg-result__label', set.ui.yourScore));
    wrap.appendChild(el('p', 'bzg-result__num', String(out.score)));

    // Result scales v2 (T-279dcc, Calum 2026-08-08): TWO comparisons — Score (lower↔higher,
    // scorePercentile from the RPC) and Speed (slower↔faster, the original paced percentile) —
    // each a titled strip + its own caption. Same >=2 population gate as v1 (n=1 renders the
    // first-player copy; DE/FR plural agreement breaks below 2 and the comparison is meaningless
    // anyway). A null scorePercentile (old Worker/RPC still live) simply omits the Score scale —
    // graceful, no error, Speed behaves exactly as v1.
    function renderScale(titleTxt, lowTxt, highTxt, pct, captionTxt) {
      var box = el('div', 'bzg-spread');
      box.appendChild(el('div', 'bzg-spread__title', titleTxt));
      var track = el('div', 'bzg-spread__track');
      var marker = el('div', 'bzg-spread__marker');
      marker.style.left = Math.max(2, Math.min(98, pct)) + '%';
      marker.setAttribute('aria-hidden', 'true');
      track.appendChild(marker);
      box.appendChild(track);
      var labels = el('div', 'bzg-spread__labels');
      labels.appendChild(el('span', 'bzg-spread__label', lowTxt));
      labels.appendChild(el('span', 'bzg-spread__label', highTxt));
      box.appendChild(labels);
      var cap = el('p', 'bzg-result__cmp');
      cap.textContent = captionTxt;
      box.appendChild(cap);
      return box;
    }
    var comparable = api && api.population != null && api.population >= 2;
    var scalesRendered = 0;
    if (comparable && api.scorePercentile != null) {
      wrap.appendChild(renderScale(
        set.scaleScoreTitle || '', set.scoreLowLabel || '', set.scoreHighLabel || '',
        api.scorePercentile,
        (set.scoreOf || '')
          .replace('{pct}', String(Math.round(api.scorePercentile)))
          .replace('{n}', String(api.population))));
      scalesRendered++;
    }
    if (comparable && api.percentile != null) {
      wrap.appendChild(renderScale(
        set.scaleSpeedTitle || '', set.spreadSlowLabel || '', set.spreadFastLabel || '',
        api.percentile,
        (set.percentileOf || '')
          .replace('{pct}', String(Math.round(api.percentile)))
          .replace('{n}', String(api.population))));
      scalesRendered++;
    }
    // Fallback preserved from v1 (5/6 QC peers converged on this): if NOTHING comparative
    // rendered — population < 2, no api at all, OR the anomalous population>=2-with-null-
    // percentiles shape — the slot never goes silently blank.
    if (scalesRendered === 0) {
      var cmp = el('p', 'bzg-result__cmp');
      cmp.textContent = (api && api.population != null && api.population < 2)
        ? set.percentileFirst
        : set.percentilePending;   // Worker unreachable, not yet deployed, or partial RPC shape
      wrap.appendChild(cmp);
    }
    mount.appendChild(wrap);

    // The segue mounts BELOW the score (tactics.md §14.4): science → score → preference → product,
    // so the product is never adjacent enough to the phenomenon to read as its remedy.
    var segueHost = el('div', 'bzg-segue-host');
    mount.appendChild(segueHost);

    var products = (root.BZGameProducts || {})[market];
    if (root.BZSegue && products) {
      try {
        // The segue ships its own stylesheet and injects it ON REQUEST (idempotent). Without this
        // call every segue class renders unstyled — 27 of them, i.e. the whole preference screen,
        // the product card and both checkout buttons as bare HTML on the highest-intent screen.
        if (root.BZSegue.injectStyles) root.BZSegue.injectStyles(document);
        new root.BZSegue({
          strings: set,
          products: products,
          storefrontBase: products.pro.storefrontBase,
          onPlayAgain: restart,
          onRoute: function (key, sel) {
            if (root.dataLayer) {
              root.dataLayer.push({
                event: 'bz_game_route', bz_product: key,
                bz_push: !!sel.push, bz_calm: !!sel.calm, bz_market: market
              });
            }
          }
        }).mount(segueHost);
      } catch (e) {
        // Never leave the highest-intent screen blank. Log loudly, keep the page usable.
        if (root.console) root.console.error('[bz-game] segue failed to mount', e);
        if (root.dataLayer) root.dataLayer.push({ event: 'bz_game_segue_failed', bz_market: market });
      }
    }
  }

  /**
   * Coarse band for the composite score (range 10-100), for analytics only.
   *
   * The RAW score must never leave the page: tactics.md 14.2 and the published privacy line
   * both commit that the score is session-only and never persisted as a customer trait.
   * A band keeps the segmentation value without shipping a per-player number. Event scope
   * only — never attach this at user scope, which is what would turn it into a trait.
   * See T-334e01.
   */
  function scoreBand(score) {
    var n = Number(score);
    if (!isFinite(n)) return 'unknown';
    if (n < 40) return '10-39';
    if (n < 60) return '40-59';
    if (n < 80) return '60-79';
    return '80-100';
  }

  /** Called by bz-game.liquid once the engine assets are on window. */
  root.BZGameBoot = function (mountEl) {
    var market = (mountEl && mountEl.getAttribute('data-market')) || 'uk';
    var endpoint = (mountEl && mountEl.getAttribute('data-api')) || '';
    var packed = root.BZGameI18N.get(market);
    if (!packed) {
      if (root.console) root.console.error('[bz-game] no string set for market', market);
      return;
    }

    /**
     * The engine's REAL config shape: flat keys, `strings` is a FLATTENED ui bag with roundNames
     * merged in, and the item pools/lexicon/word lists come from BZGamePools.build(market).
     *
     * The first version of this module invented `{ s: …, opts: { … } }`. The engine did not throw —
     * it tolerated every missing key — so the live page rendered a HUD and a GO button over a game
     * with NO strings and NO item pools. That is why the instruction body and the round name were
     * both blank and why nothing was playable (Calum, live, 2026-08-07). Mirrors harness.html
     * exactly; if that construction changes, change it here in the same commit.
     */
    function packStrings(set) {
      var s = {};
      for (var k in set.ui) {
        if (Object.prototype.hasOwnProperty.call(set.ui, k)) s[k] = set.ui[k];
      }
      s.roundNames = set.roundNames;
      return s;
    }

    function start() {
      mountEl.innerHTML = '';
      var built = root.BZGamePools.build(market);
      var game = new root.BZGame({
        market: market,
        strings: packStrings(packed),
        pools: built.pools,
        lexicon: built.lexicon,
        wordLists: built.wordLists,
        science: packed.science,
        howto: packed.howto,          // the instruction body — blank without this
        stat: packed.stat,            // localised 'your own number' sentence per card
        rounds: root.BZGame.ROUND_COUNT
      });
      game.mount(mountEl, function (out) {
        if (root.dataLayer) {
          root.dataLayer.push({ event: 'bz_game_completed', bz_market: market, bz_score_band: scoreBand(out.score) });
        }
        recordRun(endpoint, market, out.results).then(function (api) {
          renderResult(mountEl, packed, market, out, api, start);
        });
      });
    }

    if (root.dataLayer) root.dataLayer.push({ event: 'bz_game_started', bz_market: market });
    start();
  };
})(window);
